import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';

const CelebrationOverlay = ({ celebration, level, onDismiss }) => {
  const [show, setShow] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!celebration || level === 0) {
      setShow(false);
      return;
    }

    setShow(true);
    const duration = level === 1 ? 2000 : level === 2 ? 3000 : 5000;
    timerRef.current = setTimeout(() => {
      setShow(false);
      setTimeout(onDismiss, 300);
    }, duration);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celebration?._id, level, onDismiss]);

  const handleDismiss = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setShow(false);
    setTimeout(onDismiss, 300);
  };

  if (!celebration || level === 0) return null;

  const { emoji = '\uD83C\uDF89', title = 'Great job!', subtitle } = celebration;

  // Level 1: Bottom floating banner
  if (level === 1) {
    return (
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className="fixed bottom-6 left-4 right-4 z-[300] flex items-center gap-3 bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-slate-100 dark:border-gray-700 px-5 py-3.5 max-w-sm mx-auto cursor-pointer"
            onClick={handleDismiss}
          >
            <span className="text-2xl flex-shrink-0">{emoji}</span>
            <div className="min-w-0">
              <span className="text-sm font-bold text-slate-700 block truncate">{title}</span>
              {subtitle && <span className="text-xs text-slate-400 block truncate">{subtitle}</span>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Level 2: Centered card
  if (level === 2) {
    return (
      <AnimatePresence>
        {show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[300] flex items-center justify-center bg-black/20 backdrop-blur-sm"
            onClick={handleDismiss}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl px-8 py-7 text-center max-w-xs mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                className="text-5xl mb-3"
              >
                {emoji}
              </motion.div>
              <h3 className="text-lg font-black text-slate-800 mb-1">{title}</h3>
              {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  // Level 3: Full confetti + big card
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/25 backdrop-blur-sm"
          onClick={handleDismiss}
        >
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={300}
            gravity={0.25}
            initialVelocityY={20}
            colors={['#818cf8', '#f472b6', '#34d399', '#fbbf24', '#fb923c', '#a78bfa', '#22d3ee', '#f87171']}
          />

          {['\u2728', '\u2B50', '\uD83C\uDF1F', '\uD83D\uDCAB', '\uD83C\uDF8A'].map((e, i) => (
            <motion.div
              key={i}
              className="absolute text-2xl pointer-events-none"
              style={{ left: `${10 + i * 20}%`, top: `${15 + (i % 3) * 25}%` }}
              animate={{ y: [0, -20, 0], opacity: [0.4, 1, 0.4], scale: [0.8, 1.2, 0.8] }}
              transition={{ duration: 2, delay: i * 0.3, repeat: Infinity }}
            >
              {e}
            </motion.div>
          ))}

          <motion.div
            initial={{ scale: 0.3, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.5, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl px-10 py-8 text-center max-w-sm mx-4 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 opacity-60" />
            <div className="relative z-10">
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="text-6xl mb-4"
              >
                {emoji}
              </motion.div>
              <h3 className="text-2xl font-black text-slate-800 mb-2">{title}</h3>
              {subtitle && <p className="text-base text-slate-500 mb-2">{subtitle}</p>}
              <p className="text-[10px] text-slate-400 mt-3">Tap anywhere to continue</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CelebrationOverlay;
